import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Building2, Package, Send, CheckCircle, XCircle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
  ConfirmDialog,
} from '@/components/common';
import { purchaseOrdersApi, PurchaseOrder, PurchaseOrderItem } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusMap: Record<string, 'draft' | 'pending' | 'approved' | 'rejected' | 'processing' | 'completed'> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'rejected',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ORDERED: 'Ordered',
  PARTIALLY_RECEIVED: 'Partially Received',
  RECEIVED: 'Received',
  CANCELLED: 'Cancelled',
};

export default function PurchaseOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action?: 'submit' | 'issue' | 'cancel';
  }>({ open: false });
  const [processing, setProcessing] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await purchaseOrdersApi.getById(id);
      setOrder(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleAction = async () => {
    if (!order || !actionDialog.action) return;

    try {
      setProcessing(true);
      switch (actionDialog.action) {
        case 'submit':
          await purchaseOrdersApi.submit(order.id);
          toast.success('PO submitted for approval');
          break;
        case 'issue':
          await purchaseOrdersApi.issue(order.id);
          toast.success('PO issued to supplier');
          break;
        case 'cancel':
          await purchaseOrdersApi.cancel(order.id);
          toast.success('PO cancelled');
          break;
      }
      setActionDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!order) {
    return null;
  }

  const items = order.items || [];
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalReceived = items.reduce((sum, item) => sum + item.receivedQty, 0);
  const receivedPercentage = totalItems > 0 ? Math.round((totalReceived / totalItems) * 100) : 0;

  const itemColumns: Column<PurchaseOrderItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{item.item?.name || '-'}</div>
            <div className="text-sm text-muted-foreground">{item.item?.sku}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Ordered',
      render: (item) => (
        <span className="font-medium">{item.quantity} {item.item?.unit}</span>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      render: (item) => (
        <span className={item.receivedQty >= item.quantity ? 'text-emerald-600' : 'text-muted-foreground'}>
          {item.receivedQty} {item.item?.unit}
        </span>
      ),
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      render: (item) => (
        <span className="text-muted-foreground">{formatCurrency(item.unitPrice)}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      render: (item) => (
        <span className="font-medium">
          {formatCurrency((item.unitPrice || 0) * item.quantity)}
        </span>
      ),
    },
  ];

  const getActionButtons = () => {
    switch (order.status) {
      case 'DRAFT':
        return (
          <>
            <Button variant="outline" onClick={() => setActionDialog({ open: true, action: 'cancel' })}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={() => setActionDialog({ open: true, action: 'submit' })}>
              <Send className="mr-2 h-4 w-4" />
              Submit for Approval
            </Button>
          </>
        );
      case 'APPROVED':
        return (
          <Button onClick={() => setActionDialog({ open: true, action: 'issue' })}>
            <Truck className="mr-2 h-4 w-4" />
            Issue to Supplier
          </Button>
        );
      case 'ORDERED':
      case 'PARTIALLY_RECEIVED':
        return (
          <Button>
            <CheckCircle className="mr-2 h-4 w-4" />
            Receive Items
          </Button>
        );
      default:
        return null;
    }
  };

  const getDialogContent = () => {
    switch (actionDialog.action) {
      case 'submit':
        return {
          title: 'Submit for Approval',
          description: 'This will send the purchase order for approval. Are you sure?',
          confirmLabel: 'Submit',
          variant: 'default' as const,
        };
      case 'issue':
        return {
          title: 'Issue to Supplier',
          description: 'This will issue the purchase order to the supplier. Are you sure?',
          confirmLabel: 'Issue',
          variant: 'default' as const,
        };
      case 'cancel':
        return {
          title: 'Cancel Purchase Order',
          description: 'This will cancel the purchase order. This action cannot be undone.',
          confirmLabel: 'Cancel PO',
          variant: 'destructive' as const,
        };
      default:
        return { title: '', description: '', confirmLabel: '', variant: 'default' as const };
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/purchase-orders')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader
          title={order.poNumber}
          description={`Created: ${formatDate(order.createdAt)}`}
          breadcrumbs={[
            { label: 'Purchase Orders', href: '/purchase-orders' },
            { label: order.poNumber },
          ]}
          actions={<div className="flex gap-2">{getActionButtons()}</div>}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Amount"
          value={formatCurrency(order.totalAmount)}
          icon={<FileText className="h-5 w-5" />}
        />
        <MetricCard
          label="Items"
          value={items.length}
          icon={<Package className="h-5 w-5" />}
        />
        <MetricCard
          label="Received"
          value={`${receivedPercentage}%`}
          trend={receivedPercentage === 100 ? { direction: 'up', value: '', text: 'complete' } : undefined}
        />
        <MetricCard
          label="Status"
          value={statusLabels[order.status] || order.status}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Order Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Order Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Status</span>
              <StatusBadge status={statusMap[order.status] || 'draft'} />
            </div>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Supplier</span>
              </div>
              <p className="text-muted-foreground pl-6">{order.supplier?.name || '-'}</p>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Order Date</span>
              <span>{formatDate(order.orderDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expected Date</span>
              <span>{formatDate(order.expectedDate)}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Currency</span>
              <span>{order.currency}</span>
            </div>
            {order.notes && (
              <>
                <Separator />
                <div className="space-y-2">
                  <span className="text-sm font-medium">Notes</span>
                  <p className="text-sm text-muted-foreground">{order.notes}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Line Items</CardTitle>
            <CardDescription>{items.length} item(s) in this order</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              data={items}
              columns={itemColumns}
              emptyTitle="No items"
              emptyDescription="No items have been added to this order."
            />
          </CardContent>
        </Card>
      </div>

      {/* Action Dialog */}
      <ConfirmDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ open })}
        {...getDialogContent()}
        onConfirm={handleAction}
        loading={processing}
      />
    </div>
  );
}
